import { MimeType } from 'vet-bones/constants';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { FileExtension } from 'src/constants/fileExtensions';
import { getRuleModel } from 'src/containers/ProjectDiagram/utils/rules/getRuleModel';
import { CURRENT_MIGRATION_VERSION } from 'src/reducers/migrations/constants';
import { dataSourceTypeDescriptionSelector } from 'src/selectors/dataSourceTypeDescription';
import { Connection } from 'src/store/connections/types';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import { exportModel } from 'src/utils/export/model';
import { getAllProjectNamespaces } from 'src/utils/export/namespaces';
import { exportResources } from 'src/utils/export/resources';
import { exportRules } from 'src/utils/export/rules';
import { getValidMappingsByResource } from 'src/utils/export/validateMappingsByResource';
import { encodeProject } from 'src/utils/exportProject/encodeProject';
import { ExportToFileOptions, exportToZipFile } from 'src/utils/exportToFile';
import { stringToFilename } from 'src/utils/stringToFilename';

export const exportProject =
  (projectId: string, connection: Connection | null) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const state = getState();
    const project = state.projects.projectsById[projectId];
    const typeDescription =
      dataSourceTypeDescriptionSelector(connection)(state);

    if (!project) {
      return;
    }

    const { diagram, name } = project;
    if (!diagram) {
      return;
    }

    const namespaces = getAllProjectNamespaces(project);
    const modelFile = exportModel(
      name,
      diagram.model,
      project.propertyType,
      namespaces
    );
    const ruleModel = getRuleModel(diagram.model);
    const rulesFile = exportRules(
      name,
      ruleModel,
      diagram.model.modelData.rulesById,
      namespaces
    );

    const { classMappingsByResource, relationshipMappingsByResource } =
      getValidMappingsByResource(diagram.model);
    const { fileUploadExports, virtualGraphExports } = exportResources({
      project,
      classMappingsByResource,
      relationshipMappingsByResource,
      typeDescription,
    });

    const fileUploadFiles = fileUploadExports.flatMap(({ folderName, files }) =>
      Object.values(files).map((file) => ({
        ...file,
        filename: `${folderName}/${file.filename}`,
      }))
    );
    const virtualGraphFiles = virtualGraphExports.flatMap(
      ({ folderName, files }) =>
        Object.values(files).map((file) => ({
          ...file,
          filename: `${folderName}/${file.filename}`,
        }))
    );

    const projectContents = encodeProject(project, CURRENT_MIGRATION_VERSION);
    if (!projectContents) {
      // if we fail to generate the project file, fail the whole export
      dispatch(projectsActionCreators.exportedProject(projectId, null));
      return;
    }

    const projectFileName = stringToFilename(name);
    const designerProjectFile = {
      contents: projectContents,
      filename: `${projectFileName}${FileExtension.STARDOG_DESIGNER}`,
      mimeType: MimeType.TEXT,
    };

    const zipFileName = `${projectFileName}${FileExtension.ZIP}`;
    await exportToZipFile({
      filename: zipFileName,
      files: [
        modelFile,
        rulesFile,
        designerProjectFile,
        ...fileUploadFiles,
        ...virtualGraphFiles,
      ].filter((file): file is ExportToFileOptions => Boolean(file)),
    });

    dispatch(projectsActionCreators.exportedProject(projectId, zipFileName));
  };
